"use client";

import { createLogger } from "@/lib/logger";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Heart, Loader2, Play, Plus, Sparkles, Volume2, X } from "lucide-react";
import Link from "next/link";
import * as React from "react";

const logger = createLogger("flashcard-tooltip");

export interface FlashcardData {
  ipaSpelling: Record<string, string>;
  translation: string;
  exampleSentences: Array<{
    sentence: string;
    translation: string;
  }>;
  grammarPoints: Array<{
    title: string;
    explanation: string;
  }>;
  vocabulary: Array<{
    word: string;
    partOfSpeech: string;
    definition: string;
    usage?: string;
  }>;
}

interface FlashcardTooltipProps {
  sentence: string;
  position: { x: number; y: number };
  onClose: () => void;
  targetLanguage: string;
  instructionLanguage: string;
  languageLevel?: string;
  userId?: string;
  onSave?: (sentence: string, data: FlashcardData) => Promise<void>;
  onFavorite?: (sentence: string) => Promise<void>;
  isFavorited?: boolean;
  isSaved?: boolean;
}

export function FlashcardTooltip({
  sentence,
  position,
  onClose,
  targetLanguage,
  instructionLanguage,
  languageLevel = "intermediate",
  userId,
  onSave,
  onFavorite,
  isFavorited = false,
  isSaved = false,
}: FlashcardTooltipProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<FlashcardData | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [favoriting, setFavoriting] = React.useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  // Calculate smart positioning to stay within viewport
  const [adjustedPosition, setAdjustedPosition] = React.useState(position);

  React.useEffect(() => {
    if (!tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.x;
    let y = position.y;

    // Adjust horizontal position
    if (x + rect.width > viewportWidth - 20) {
      x = viewportWidth - rect.width - 20;
    }
    if (x < 20) {
      x = 20;
    }

    // Adjust vertical position
    if (y + rect.height > viewportHeight - 20) {
      y = Math.max(20, position.y - rect.height - 20);
    }

    setAdjustedPosition({ x, y });
  }, [position]);

  // Fetch flashcard data from API
  React.useEffect(() => {
    const fetchFlashcardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch("/api/generate-flashcard", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sentence,
            targetLanguage,
            instructionLanguage,
            languageLevel,
            userId,
          }),
        });

        if (!response.ok) {
          const errorData = (await response.json()) as any;
          throw new Error(errorData.error || "Failed to generate flashcard");
        }

        const result = (await response.json()) as any;
        setData(result.data);
      } catch {
        setError("We couldn't load the language details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlashcardData();
  }, [sentence, targetLanguage, instructionLanguage, languageLevel, userId]);

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sentence);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error("Failed to copy", { error: err instanceof Error ? err.message : String(err) });
    }
  };

  // Handle text-to-speech
  const handleSpeak = () => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(sentence);

      // Try to set language based on targetLanguage
      const langCode = targetLanguage.toLowerCase().includes("spanish")
        ? "es-ES"
        : targetLanguage.toLowerCase().includes("french")
          ? "fr-FR"
          : targetLanguage.toLowerCase().includes("german")
            ? "de-DE"
            : targetLanguage.toLowerCase().includes("italian")
              ? "it-IT"
              : targetLanguage.toLowerCase().includes("dutch")
                ? "nl-NL"
                : targetLanguage.toLowerCase().includes("portuguese")
                  ? "pt-PT"
                  : targetLanguage.toLowerCase().includes("russian")
                    ? "ru-RU"
                    : targetLanguage.toLowerCase().includes("japanese")
                      ? "ja-JP"
                      : targetLanguage.toLowerCase().includes("chinese")
                        ? "zh-CN"
                        : "en-US";

      utterance.lang = langCode;
      speechSynthesis.speak(utterance);
    }
  };

  // Handle save flashcard
  const handleSave = async () => {
    // Show upgrade prompt for unauthenticated users
    if (!userId) {
      setShowUpgradePrompt(true);
      return;
    }

    if (!onSave || !data || saving) return;

    try {
      setSaving(true);
      await onSave(sentence, data);
    } catch (err) {
      logger.error("Failed to save flashcard", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle favorite toggle
  const handleFavorite = async () => {
    if (!onFavorite || favoriting) return;

    try {
      setFavoriting(true);
      await onFavorite(sentence);
    } catch (err) {
      logger.error("Failed to toggle favorite", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setFavoriting(false);
    }
  };

  // Close on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        ref={tooltipRef}
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="fixed z-[100] w-[480px] max-w-[calc(100vw-40px)] rounded-2xl shadow-2xl overflow-hidden"
        style={{
          left: `${adjustedPosition.x}px`,
          top: `${adjustedPosition.y}px`,
        }}
      >
        {/* Dark themed container matching app design */}
        <div className="bg-gradient-to-br from-[#2a2d42]/95 to-[#23263a]/95 backdrop-blur-xl border border-orange-500/20">
          {/* Header with sentence and action buttons */}
          <div className="px-5 py-4 border-b border-orange-500/10">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 text-base font-medium text-gray-100 leading-relaxed">
                {sentence}
              </div>
              <button
                onClick={onClose}
                className="shrink-0 p-1 hover:bg-orange-500/10 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            {/* Action buttons row */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleSpeak}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-lg transition-colors text-sm font-medium"
                aria-label="Play pronunciation"
              >
                <Volume2 size={16} />
                <span>Play</span>
              </button>

              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-700/70 text-gray-300 rounded-lg transition-colors text-sm font-medium"
                aria-label="Copy word"
              >
                <Copy size={16} />
                <span>{copied ? "Copied!" : "Copy"}</span>
              </button>

              {onFavorite && (
                <button
                  onClick={handleFavorite}
                  disabled={favoriting}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${
                    isFavorited
                      ? "bg-pink-500/20 text-pink-400 hover:bg-pink-500/30"
                      : "bg-gray-700/50 hover:bg-gray-700/70 text-gray-300"
                  }`}
                  aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart size={16} className={isFavorited ? "fill-current" : ""} />
                </button>
              )}

              {/* Show upgrade prompt for unauthenticated users */}
              {showUpgradePrompt && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute inset-0 bg-gradient-to-br from-[#2a2d42]/98 to-[#23263a]/98 backdrop-blur-sm flex flex-col items-center justify-center p-6 rounded-t-2xl z-10"
                >
                  <Sparkles className="w-10 h-10 text-orange-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2 text-center">
                    Save Flashcards Forever! 🐣
                  </h3>
                  <p className="text-sm text-gray-300 text-center mb-4 max-w-[280px]">
                    Create a free account to save flashcards to your library and track your
                    progress.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowUpgradePrompt(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
                    >
                      Maybe later
                    </button>
                    <Link
                      href="/auth"
                      className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-400 hover:to-pink-400 text-white font-semibold rounded-lg transition-all shadow-lg shadow-orange-500/20"
                    >
                      <Sparkles size={16} />
                      Sign up free
                    </Link>
                  </div>
                </motion.div>
              )}

              {onSave && !isSaved && (
                <button
                  onClick={handleSave}
                  disabled={saving || !data}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700/50 text-white disabled:text-gray-500 rounded-lg transition-colors text-sm font-medium ml-auto"
                  aria-label="Save flashcard"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  <span>{saving ? "Saving..." : "Save"}</span>
                </button>
              )}

              {isSaved && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium ml-auto">
                  <span>✓ Saved</span>
                </div>
              )}
            </div>
          </div>

          {/* Content area */}
          <div className="px-5 py-4 max-h-[60vh] overflow-y-auto overscroll-contain">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              </div>
            )}

            {error && (
              <div className="text-center py-6">
                <div className="text-red-400 mb-2">Failed to load flashcard</div>
                <div className="text-sm text-gray-400">{error}</div>
              </div>
            )}

            {data && (
              <div className="space-y-4">
                {/* IPA Pronunciation */}
                {Object.keys(data.ipaSpelling).length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-orange-400 mb-2">
                      Pronunciation
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(data.ipaSpelling).map(([word, ipa]) => (
                        <div
                          key={word}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a1d2e] rounded-lg border border-orange-500/10"
                        >
                          <span className="text-sm text-gray-200 font-medium">{word}</span>
                          <span className="text-xs text-orange-300 font-mono">[{ipa}]</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Translation */}
                {data.translation && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-orange-400 mb-2">
                      Translation
                    </h3>
                    <p className="text-sm text-gray-200 leading-relaxed italic">
                      {data.translation}
                    </p>
                  </div>
                )}

                {/* Vocabulary */}
                {data.vocabulary && data.vocabulary.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-orange-400 mb-2">
                      Vocabulary
                    </h3>
                    <div className="space-y-2">
                      {data.vocabulary.map((vocab, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-[#1a1d2e] rounded-lg border border-orange-500/10"
                        >
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-sm font-semibold text-orange-300">
                              {vocab.word}
                            </span>
                            <span className="text-xs text-gray-500 italic">
                              ({vocab.partOfSpeech})
                            </span>
                          </div>
                          <p className="text-sm text-gray-300">{vocab.definition}</p>
                          {vocab.usage && (
                            <p className="text-xs text-gray-400 mt-1 italic">💡 {vocab.usage}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Grammar Points */}
                {data.grammarPoints && data.grammarPoints.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-orange-400 mb-2">
                      Grammar
                    </h3>
                    <div className="space-y-2">
                      {data.grammarPoints.map((point, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-[#1a1d2e] rounded-lg border border-orange-500/10"
                        >
                          <div className="text-sm font-semibold text-orange-300 mb-1">
                            {point.title}
                          </div>
                          <p className="text-sm text-gray-300 leading-relaxed">
                            {point.explanation}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Example Sentences */}
                {data.exampleSentences && data.exampleSentences.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-orange-400 mb-2">
                      Examples
                    </h3>
                    <div className="space-y-2">
                      {data.exampleSentences.map((example, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-[#1a1d2e] rounded-lg border border-orange-500/10"
                        >
                          <p className="text-sm text-gray-200 mb-1">{example.sentence}</p>
                          <p className="text-xs text-gray-400 italic">{example.translation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
